import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ProfileApiService } from '../apiCalls/profile-api.service';
import { CookieKeys, LocalStorageKeys } from '../shared/key-const';
import { RoutesConst } from '../shared/route-const';
import { AuthApiService } from './../apiCalls/auth-api.service';
import { GetToken, LoginFormValue } from './login/login';

export interface UserProfile {
  AllowImpersonation: boolean;
  Email: string;
  FirstName: string;
  LastName: string;
  Initials: string;
  PhoneNumber: string;
  ShowUserManagement: boolean;
}

interface AuthState {
  isAuth: boolean;
  userName: string;
  user: UserProfile;
  authenticationChecked: boolean;
  impersonation_originalUserName: string | null;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  // Dependency injection via Angular's inject() for better tree-shaking and testability
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly profileApiSvc = inject(ProfileApiService);
  private readonly authApiSvc = inject(AuthApiService);

  private readonly tokenExpires = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d;
  })();

  // Reactive authentication state
  authentication = signal<AuthState>({
    isAuth: false,
    userName: '',
    user: {
      AllowImpersonation: false,
      Email: '',
      FirstName: '',
      LastName: '',
      Initials: '',
      PhoneNumber: '',
      ShowUserManagement: false,
    },
    authenticationChecked: false,
    impersonation_originalUserName: null,
  });

  // Observable for components to subscribe to authentication state changes
  authState$ = toObservable(this.authentication);

  constructor() {
    // Check URL, if Url Logout (Skin Route), Logout else Load Initial Auth State
    const isLogout = window.location.pathname.includes(RoutesConst.logout);
    if (isLogout) {
      this.logout();
      return;
    }
    this.loadInitialAuthState();
  }

  /**
   * Loads initial authentication state from LocalStorage and Cookies.
   * Also checks if user is authenticated and loads profile if so.
   */
  private async loadInitialAuthState() {
    const userName = localStorage.getItem(LocalStorageKeys.ExtranetUserName) || '';
    const impersonationOriginalUserName = localStorage.getItem(LocalStorageKeys.ExtranetAuth_GoBack_UserName) || '';
    const accessToken = this.cookieService.get(CookieKeys.ExtranetAccessToken);

    if (userName && accessToken) {
      this.authentication.update((state) => ({
        ...state,
        isAuth: true,
        userName,
        impersonation_originalUserName: impersonationOriginalUserName,
      }));
      await this.fillUserProfile();
    } else {
      this.authentication.update((state) => ({
        ...state,
        isAuth: false,
        userName: '',
        impersonation_originalUserName: null,
      }));
    }
    this.messageToSkin();
  }

  /**
   * Loads user profile from API and updates the authentication state.
   * Stores the user profile in localStorage for persistence.
   */
  private fillUserProfile(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.profileApiSvc.getUserProfile().subscribe({
        next: (userProfile) => {
          this.authentication.update((state) => ({
            ...state,
            authenticationChecked: true,
            user: {
              ...userProfile,
              Initials: (userProfile.FirstName?.charAt(0) ?? '') + (userProfile.LastName?.charAt(0) ?? ''),
            },
          }));
          localStorage.setItem(LocalStorageKeys.ExtranetUser, JSON.stringify(this.authentication().user));
          resolve();
        },
        error: (error) => {
          console.error('Error fetching user profile:', error);
          reject(error);
        },
      });
    });
  }

  /**
   * Handles user login: updates state, sets cookies/localStorage, loads profile and navigates home.
   */
  async login(loginValue: LoginFormValue, token: GetToken) {
    this.authentication.update((state) => ({
      ...state,
      userName: loginValue.email,
      isAuth: true,
    }));

    // Store token and username in cookie and localStorage
    this.cookieService.delete(CookieKeys.ExtranetAccessToken, '/');
    this.cookieService.set(CookieKeys.ExtranetAccessToken, token.access_token, { expires: this.tokenExpires, path: '/' });
    localStorage.setItem(LocalStorageKeys.ExtranetUserName, loginValue.email);
    localStorage.setItem(LocalStorageKeys.ExtranetLastUsedUserName, loginValue.email);

    await this.fillUserProfile();

    this.messageToSkin();

    this.router.navigate([`/`, RoutesConst.home]);
  }

  /**
   * Handles user logout: resets state, clears cookies/localStorage and navigates home.
   */
  logout() {
    this.authentication.update((state) => ({
      ...state,
      isAuth: false,
      userName: '',
      user: {
        AllowImpersonation: false,
        Email: '',
        FirstName: '',
        LastName: '',
        Initials: '',
        PhoneNumber: '',
        ShowUserManagement: false,
      },
      authenticationChecked: false,
      impersonation_originalUserName: null,
    }));

    this.cookieService.delete(CookieKeys.ExtranetAccessToken, '/');
    localStorage.removeItem(LocalStorageKeys.ExtranetUserName);
    localStorage.removeItem(LocalStorageKeys.ExtranetUser);
    localStorage.removeItem(LocalStorageKeys.ExtranetAuth_GoBack_AccessToken);
    localStorage.removeItem(LocalStorageKeys.ExtranetAuth_GoBack_UserName);

    this.messageToSkin();

    this.router.navigate([`/`, RoutesConst.login]);
  }

  /**
   * Starts impersonation for the given userName.
   * Stores current authentication info for later restoration.
   */
  impersonateAsFn(userName: string) {
    this.authApiSvc.impersonateAsApiCall(userName).subscribe({
      next: async (token) => {
        // Save "go back" info to localStorage for restoration after impersonation
        localStorage.setItem(LocalStorageKeys.ExtranetAuth_GoBack_AccessToken, this.cookieService.get(CookieKeys.ExtranetAccessToken));
        localStorage.setItem(LocalStorageKeys.ExtranetAuth_GoBack_UserName, this.authentication().userName);

        // Update authentication state to impersonated user
        this.authentication.update((state) => ({
          ...state,
          userName,
          isAuth: true,
          impersonation_originalUserName: localStorage.getItem(LocalStorageKeys.ExtranetAuth_GoBack_UserName) || '',
        }));
        // Set new access token and username

        this.cookieService.delete(CookieKeys.ExtranetAccessToken, '/');
        this.cookieService.set(CookieKeys.ExtranetAccessToken, token.access_token, { expires: this.tokenExpires, path: '/' });
        localStorage.setItem(LocalStorageKeys.ExtranetUserName, userName);

        await this.fillUserProfile();
        this.messageToSkin();
        this.router.navigate([`/`, RoutesConst.home]);
      },
      error: (error) => {
        console.error('Error fetching user profile:', error);
      },
    });
  }

  /**
   * Ends impersonation session and restores the previous user's credentials and state.
   */
  async exitImpersonation() {
    // Restore previous authentication info from localStorage
    const goBackAccessToken = localStorage.getItem(LocalStorageKeys.ExtranetAuth_GoBack_AccessToken) || '';
    const goBackUserName = localStorage.getItem(LocalStorageKeys.ExtranetAuth_GoBack_UserName) || '';

    this.cookieService.delete(CookieKeys.ExtranetAccessToken, '/');
    this.cookieService.set(CookieKeys.ExtranetAccessToken, goBackAccessToken, { expires: this.tokenExpires, path: '/' });

    this.authentication.update((state) => ({
      ...state,
      userName: goBackUserName,
      isAuth: true,
      impersonation_originalUserName: null,
    }));

    localStorage.removeItem(LocalStorageKeys.ExtranetAuth_GoBack_UserName);
    localStorage.setItem(LocalStorageKeys.ExtranetUserName, goBackUserName);

    await this.fillUserProfile();
    this.messageToSkin();

    this.router.navigate([`/`, RoutesConst.home]);
  }

  /**
   * Use for Skin to send authentication state and User
   */
  private messageToSkin() {
    const message = {
      type: 'auth',
      user: this.authentication().user,
      authentication: this.authentication(),
    };
    window.parent.postMessage(JSON.stringify(message), '*');
  }
}
